import Shepherd from 'shepherd.js';
import { TOUR_BUTTONS } from '../shepherd-defaults';
import { ensureShepherdModalOverlayHidden, registerInteractiveTourGuards } from '../shepherd-interactive-step';
import {
  registerTourHighlightedFieldLockGuards,
  releaseAllShepherdTourFieldLocks,
  SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
  withTourFieldLockTipMessage,
} from '../shepherd-tour-field-lock';
import { waitForElement } from '../shepherd.utils';

type ShepherdTour = InstanceType<typeof Shepherd.Tour>;

export const TRAINING_BOOK_SESSION_TOUR = 'training-book-session';

export const TRAINING_SESSION_PAYMENT_STEP_ID = 'training-session-payment';

/** Payment highlight is view-only on this step (date step stays interactive). */
export const TRAINING_SESSION_PAYMENT_STEP_BODY_CLASS = 'shepherd-training-session-payment-step';

/** Set when list tour step 2 OK runs or when booking while list tour is active; cleared when session tour starts. */
export const TRAINING_BOOK_SESSION_TOUR_PENDING_KEY = 'app_training_book_session_tour_pending';

const TRAINING_PAYMENT_SECTION_SELECTOR = '[data-tour="training-payment-section"]';

const STRIPE_CARD_INTERACTION_SELECTOR =
  '.stripe-card-element, #card-element, #card-element-modal, .stripe-card-element iframe';

let paymentInteractionBlocker: ((e: Event) => void) | null = null;

function resolveTrainingPaymentSectionTarget(): HTMLElement | null {
  const nodes = document.querySelectorAll(TRAINING_PAYMENT_SECTION_SELECTOR);
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (node instanceof HTMLElement && node.getClientRects().length > 0) {
      return node;
    }
  }
  const first = nodes[0];
  return first instanceof HTMLElement ? first : null;
}

function isStripeOrFieldTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  if (target.closest('.shepherd-element, .shepherd-modal-overlay-container')) {
    return false;
  }
  const root = target.closest(TRAINING_PAYMENT_SECTION_SELECTOR);
  if (!root) {
    return false;
  }
  if (target.closest(STRIPE_CARD_INTERACTION_SELECTOR)) {
    return true;
  }
  return !!target.closest(
    'input:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]), textarea, select',
  );
}

function lockTrainingPaymentSectionInteraction(): void {
  if (paymentInteractionBlocker) {
    return;
  }
  paymentInteractionBlocker = (e: Event) => {
    if (!isStripeOrFieldTarget(e.target)) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  };
  document.addEventListener('click', paymentInteractionBlocker, true);
  document.addEventListener('pointerdown', paymentInteractionBlocker, true);
  document.addEventListener('mousedown', paymentInteractionBlocker, true);
  document.addEventListener('focusin', paymentInteractionBlocker, true);
}

function unlockTrainingPaymentSectionInteraction(): void {
  if (!paymentInteractionBlocker) {
    return;
  }
  document.removeEventListener('click', paymentInteractionBlocker, true);
  document.removeEventListener('pointerdown', paymentInteractionBlocker, true);
  document.removeEventListener('mousedown', paymentInteractionBlocker, true);
  document.removeEventListener('focusin', paymentInteractionBlocker, true);
  paymentInteractionBlocker = null;
}

function syncTrainingSessionPaymentStepLock(active: boolean): void {
  document.body.classList.toggle(TRAINING_SESSION_PAYMENT_STEP_BODY_CLASS, active);
  if (active) {
    lockTrainingPaymentSectionInteraction();
    return;
  }
  unlockTrainingPaymentSectionInteraction();
}

/** Tear down session-tour body classes and payment locks (navigation / stopTourIfId). */
export function clearTrainingBookSessionTourUi(): void {
  syncTrainingSessionPaymentStepLock(false);
  releaseAllShepherdTourFieldLocks();
  ensureShepherdModalOverlayHidden();
}

export function buildTrainingBookSessionTour(tour: ShepherdTour): void {
  registerInteractiveTourGuards(tour);
  registerTourHighlightedFieldLockGuards(
    tour,
    [TRAINING_SESSION_PAYMENT_STEP_ID],
    resolveTrainingPaymentSectionTarget,
  );

  tour.on('show', (ev: { step?: { id?: string } }) => {
    syncTrainingSessionPaymentStepLock(ev?.step?.id === TRAINING_SESSION_PAYMENT_STEP_ID);
  });

  tour.on('hide', (ev: { step?: { id?: string } }) => {
    if (ev?.step?.id === TRAINING_SESSION_PAYMENT_STEP_ID) {
      syncTrainingSessionPaymentStepLock(false);
    }
  });

  tour.on('complete', () => clearTrainingBookSessionTourUi());
  tour.on('cancel', () => clearTrainingBookSessionTourUi());

  tour.addStep({
    id: 'training-session-select-date',
    title: 'Tip',
    text: 'Click and select a time that works best for your schedule.',
    attachTo: { element: '[data-tour="training-select-date-section"]', on: 'bottom' },
    buttons: [TOUR_BUTTONS.skip, TOUR_BUTTONS.ok],
    canClickTarget: true,
    beforeShowPromise() {
      return waitForElement('[data-tour="training-select-date-section"]', 6000).then(() => undefined);
    },
  });

  // No `showOn`: otherwise Shepherd skips this step if payment UI is not in the DOM yet
  // (e.g. right after the date step) and the tour ends early.
  tour.addStep({
    id: TRAINING_SESSION_PAYMENT_STEP_ID,
    title: 'Tip',
    text: withTourFieldLockTipMessage('Payments are securely processed through Selfwish.'),
    attachTo: { element: '[data-tour="training-payment-section"]', on: 'top' },
    buttons: [TOUR_BUTTONS.skip, TOUR_BUTTONS.finish],
    ...SHEPHERD_TOUR_READONLY_HIGHLIGHT_STEP_OPTIONS,
    beforeShowPromise() {
      return waitForElement('[data-tour="training-payment-section"]', 120000).then(() => {
        lockTrainingPaymentSectionInteraction();
        return undefined;
      });
    },
  });
}
